// src/components/creditnote/CreditNoteNew.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { decryptData } from "../../utils/encrypt";

import {
  SALES_INVOICE_DETAILS_API,       // POST { id }
  SALES_INVOICE_DETAILS_BY_NO_API, // POST { tally_invoice_no }
  CREDIT_NOTE_CREATE_API,          // POST payload (server generates CN no if blank)
} from "../../config/api";

const fmtN = (v, d = 3) => Number(v ?? 0).toFixed(d);
const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const todayYMD = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function CreditNoteNew() {
  const navigate = useNavigate();

  // auth/org
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  // invoice lookup
  const [lookup, setLookup] = useState("");
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [invoice, setInvoice] = useState(null); // full payload from API (includes existing_credit_notes)

  // CN header
  const [credit, setCredit] = useState({
    credit_note_no: "",
    credit_date: todayYMD(),
    reason: "",
    remarks: "",
  });

  // line entries
  const [lines, setLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("suyog_user");
      if (stored) {
        const u = decryptData(stored);
        setUserId(u?.user_id || null);
      }
    } catch {}
    try {
      const storedOrg = localStorage.getItem("suyog_selected_org");
      if (storedOrg) {
        const o = JSON.parse(storedOrg);
        setOrgId(o?.value || null);
      }
    } catch {}
  }, []);

  const loadInvoice = async () => {
    if (!lookup.trim()) {
      alert("Enter Invoice No (e.g., T-1234/25-26) or Invoice Item ID.");
      return;
    }
    setLoadingInvoice(true);
    setInvoice(null);
    setLines([]);
    try {
      let json;
      if (/^\d+$/.test(lookup.trim())) {
        // numeric → treat as invoice item id (your /details.php expects {id})
        const res = await fetch(SALES_INVOICE_DETAILS_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: parseInt(lookup.trim(), 10) }),
        });
        json = await res.json();
      } else {
        // non-numeric → treat as tally invoice number
        const res = await fetch(SALES_INVOICE_DETAILS_BY_NO_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tally_invoice_no: lookup.trim() }),
        });
        json = await res.json();
      }
      if (!json?.success) throw new Error(json?.error || "Invoice not found");
      const data = json.data;

      const items = Array.isArray(data.items) ? data.items : [];
      const mapped = items.map((it) => {
        const credited = safeNum(it.credited_qty);
        const remaining = Number.isFinite(safeNum(it.remaining_qty))
          ? safeNum(it.remaining_qty)
          : Math.max(0, safeNum(it.qty) - credited);

        return {
          selected: remaining > 0, // pre-select only when there is balance
          invoice_item_id: it.id,
          material_name: it.material_name,
          unit: it.unit || "",
          gst: safeNum(it.gst),         // %
          base_qty: safeNum(it.qty),    // invoiced
          base_rate: safeNum(it.price), // invoiced rate
          credited_qty: credited,
          remaining_qty: remaining,
          credit_qty: 0,                // user enters
          credit_rate: safeNum(it.price),
        };
      });

      setInvoice(data);
      setLines(mapped);
      setSelectAll(mapped.every((r) => r.selected));
    } catch (e) {
      console.error("loadInvoice error:", e);
      alert(e?.message || "Failed to load invoice");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setCredit((c) => ({ ...c, [name]: value }));
  };

  const toggleSelectAll = () => {
    const to = !selectAll;
    setSelectAll(to);
    setLines((prev) => prev.map((r) => ({ ...r, selected: to && r.remaining_qty > 0 })));
  };

  const fillRemainingForSelected = () => {
    setLines((prev) =>
      prev.map((r) =>
        r.selected ? { ...r, credit_qty: r.remaining_qty } : r
      )
    );
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // computed per-row amounts
  const computedRows = useMemo(() => {
    return lines.map((r) => {
      const maxQty = safeNum(r.remaining_qty);
      const qty = Math.max(0, Math.min(safeNum(r.credit_qty), maxQty));
      const rate = Math.max(0, safeNum(r.credit_rate));
      const base = qty * rate;
      const gstAmt = base * (safeNum(r.gst) / 100);
      const total = base + gstAmt;
      return { ...r, credit_qty: qty, credit_rate: rate, base, gstAmt, total };
    });
  }, [lines]);

  const totals = useMemo(() => {
    let base = 0, gst = 0, total = 0;
    for (const r of computedRows) {
      if (!r.selected || r.credit_qty <= 0) continue;
      base += r.base;
      gst += r.gstAmt;
      total += r.total;
    }
    return { base, gst, total };
  }, [computedRows]);

  const canSubmit = useMemo(() => {
    if (!userId || !orgId || !invoice) return false;
    if (!credit.credit_date || !credit.reason) return false;
    const anyLine = computedRows.some((r) => r.selected && r.credit_qty > 0);
    return anyLine && !submitting;
  }, [userId, orgId, invoice, credit, computedRows, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const items = computedRows
      .filter((r) => r.selected && r.credit_qty > 0)
      .map((r) => ({
        invoice_item_id: r.invoice_item_id,
        qty: Number(r.credit_qty),
        rate: Number(r.credit_rate),
        amount: Number(r.base),
        gst_percent: Number(r.gst),
        gst_amount: Number(r.gstAmt),
        total_amount: Number(r.total),
      }));

    const payload = {
      user_id: userId,
      org_id: orgId,
      tally_invoice_no: invoice?.tally_invoice_no,   // server uses this to resolve items
      credit_note_no: credit.credit_note_no || undefined, // optional; let server generate
      credit_date: credit.credit_date,
      reason: credit.reason,
      remarks: credit.remarks || "",
      items,
      totals: {
        base_amount: Number(totals.base),
        gst_amount: Number(totals.gst),
        grand_total: Number(totals.total),
      },
    };

    try {
      setSubmitting(true);
      const res = await fetch(CREDIT_NOTE_CREATE_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json?.success) {
        alert(`Credit Note created${json.credit_note_no ? `: ${json.credit_note_no}` : "!"}`);
        // navigate(`/creditnote/view?id=${json.id}`);
        setLookup("");
        setInvoice(null);
        setLines([]);
      } else {
        alert(`Create failed: ${json?.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("create error:", err);
      alert(`Server error: ${err?.message || err}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mt-4">
        <h4>Create Credit Note</h4>

        {/* Invoice Lookup */}
        <div className="card mt-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Invoice No or Item ID</label>
                <input
                  className="form-control"
                  placeholder="e.g. T-4846/25-26 or 12345"
                  value={lookup}
                  onChange={(e) => setLookup(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <button className="btn btn-primary w-100" onClick={loadInvoice} disabled={loadingInvoice}>
                  {loadingInvoice ? "Loading..." : "Load Invoice"}
                </button>
              </div>
              {invoice && (
                <div className="col-md-6 text-end small text-muted">
                  Loaded: <strong>{invoice.tally_invoice_no || "-"}</strong> &nbsp;|&nbsp;
                  Date: {invoice.invoice_date || "-"} &nbsp;|&nbsp;
                  Customer: {invoice.customer_name || "-"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Credit Note Header */}
        <form className="card mt-3" onSubmit={handleSubmit}>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Credit Note No (optional)</label>
                <input
                  className="form-control"
                  name="credit_note_no"
                  value={credit.credit_note_no}
                  onChange={handleHeaderChange}
                  placeholder="Leave blank to auto-generate"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Credit Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="credit_date"
                  value={credit.credit_date}
                  onChange={handleHeaderChange}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Reason</label>
                <select
                  className="form-select"
                  name="reason"
                  value={credit.reason}
                  onChange={handleHeaderChange}
                  required
                >
                  <option value="">-- Select --</option>
                  <option>Short Supply</option>
                  <option>Vehicle Diversion to</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Remarks</label>
                <input
                  className="form-control"
                  name="remarks"
                  value={credit.remarks}
                  onChange={handleHeaderChange}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          {/* Lines */}
          <div className="card-body border-top">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Invoice Items</h6>
              <div className="d-flex align-items-center gap-3">
                <div className="form-check">
                  <input
                    id="selectAll"
                    className="form-check-input"
                    type="checkbox"
                    checked={selectAll}
                    onChange={toggleSelectAll}
                    disabled={!invoice || lines.length === 0}
                  />
                  <label htmlFor="selectAll" className="form-check-label">Select all</label>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={fillRemainingForSelected}
                  disabled={!invoice || lines.length === 0}
                  title="Set Credit Qty = Remaining Qty for selected rows"
                >
                  Fill remaining for selected
                </button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-sm table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "2rem" }}></th>
                    <th>Material</th>
                    <th className="text-end">Invoiced Qty</th>
                    <th className="text-end">Credited</th>
                    <th className="text-end">Remaining</th>
                    <th>Unit</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">GST %</th>
                    <th className="text-end">Credit Qty</th>
                    <th className="text-end">Credit Rate</th>
                    <th className="text-end">Amount</th>
                    <th className="text-end">GST Amt</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(!invoice || lines.length === 0) ? (
                    <tr>
                      <td colSpan={13} className="text-center text-muted">Load an invoice to begin.</td>
                    </tr>
                  ) : computedRows.map((r, idx) => {
                      const disabled = r.remaining_qty <= 0;
                      return (
                        <tr key={r.invoice_item_id}>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={r.selected}
                              disabled={disabled}
                              onChange={(e) => updateLine(idx, { selected: e.target.checked })}
                              title={disabled ? "Nothing remaining to credit" : ""}
                            />
                          </td>
                          <td>{r.material_name}</td>
                          <td className="text-end">{fmtN(r.base_qty)}</td>
                          <td className="text-end">{fmtN(r.credited_qty)}</td>
                          <td className="text-end">{fmtN(r.remaining_qty)}</td>
                          <td>{r.unit}</td>
                          <td className="text-end">{fmtN(r.base_rate, 2)}</td>
                          <td className="text-end">{fmtN(r.gst, 2)}</td>
                          <td className="text-end" style={{ maxWidth: 120 }}>
                            <input
                              type="number"
                              step="0.001"
                              min="0"
                              max={r.remaining_qty}
                              value={r.credit_qty}
                              disabled={!r.selected || disabled}
                              className="form-control form-control-sm text-end"
                              onChange={(e) => updateLine(idx, { credit_qty: e.target.value })}
                            />
                            <div className="form-text">max {fmtN(r.remaining_qty)}</div>
                          </td>
                          <td className="text-end" style={{ maxWidth: 120 }}>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={r.credit_rate}
                              disabled={!r.selected || disabled}
                              className="form-control form-control-sm text-end"
                              onChange={(e) => updateLine(idx, { credit_rate: e.target.value })}
                            />
                          </td>
                          <td className="text-end">{fmtN(r.base, 2)}</td>
                          <td className="text-end">{fmtN(r.gstAmt, 2)}</td>
                          <td className="text-end">{fmtN(r.total, 2)}</td>
                        </tr>
                      );
                    })}
                </tbody>
                <tfoot className="table-light">
                  <tr>
                    <td colSpan={10} className="text-end fw-bold">Totals:</td>
                    <td className="text-end fw-bold">{fmtN(totals.base, 2)}</td>
                    <td className="text-end fw-bold">{fmtN(totals.gst, 2)}</td>
                    <td className="text-end fw-bold">{fmtN(totals.total, 2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Existing credit notes (if your API returns them) */}
            {Array.isArray(invoice?.existing_credit_notes) && invoice.existing_credit_notes.length > 0 && (
              <div className="mt-4">
                <h6>Existing Credit Notes</h6>
                {invoice.existing_credit_notes.map((cn) => (
                  <div key={cn.id} className="border rounded p-2 mb-2">
                    <div className="d-flex justify-content-between">
                      <div>
                        <strong>{cn.credit_note_no}</strong> &nbsp;|&nbsp; Date: {cn.credit_date} &nbsp;|&nbsp; Reason: {cn.reason}
                      </div>
                      <div>
                        Base: {fmtN(cn.base_amount,2)} &nbsp; GST: {fmtN(cn.gst_amount,2)} &nbsp; Total: <strong>{fmtN(cn.total_amount,2)}</strong>
                      </div>
                    </div>
                    {Array.isArray(cn.items) && cn.items.length > 0 && (
                      <div className="table-responsive mt-2">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Material</th>
                              <th className="text-end">Qty</th>
                              <th>Unit</th>
                              <th className="text-end">Rate</th>
                              <th className="text-end">Amount</th>
                              <th className="text-end">GST %</th>
                              <th className="text-end">GST Amt</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cn.items.map((it) => (
                              <tr key={it.id}>
                                <td>{it.material_name}</td>
                                <td className="text-end">{fmtN(it.qty)}</td>
                                <td>{it.unit || ""}</td>
                                <td className="text-end">{fmtN(it.rate,2)}</td>
                                <td className="text-end">{fmtN(it.amount,2)}</td>
                                <td className="text-end">{fmtN(it.gst_percent,2)}</td>
                                <td className="text-end">{fmtN(it.gst_amount,2)}</td>
                                <td className="text-end">{fmtN(it.total_amount,2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-success" disabled={!canSubmit}>
                {submitting ? "Creating..." : "Create Credit Note"}
              </button>
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </>
  );
}
